import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  CreditCardIcon,
  QrCodeIcon,
  CheckCircleIcon,
  LinkIcon,
  SparklesIcon,
  ArrowUpIcon,
  CalendarIcon,
  ClockIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

export default function PaymentGatewaysPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('saas_subscription'); // 'saas_subscription' | 'gym_gateway' | 'member_link'
  const [settings, setSettings] = useState(null);
  const [currentSub, setCurrentSub] = useState(null);
  const [saasHistory, setSaasHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [paying, setPaying] = useState(false);

  // Available SaaS Subscription Plans
  const saasPlans = [
    {
      name: 'Basic Tier',
      price: 499,
      features: [
        'Up to 100 Members',
        'Core Dashboard & Analytics',
        'QR Code Attendance'
      ]
    },
    {
      name: 'Pro Tier',
      price: 999,
      recommended: true,
      features: [
        'Unlimited Active Members',
        'Biometric Device Sync',
        'WhatsApp Auto Alerts',
        'Inventory & Stock Tools'
      ]
    },
    {
      name: 'Enterprise Chain',
      price: 2499,
      features: [
        'Multi-Branch Support',
        'Custom Dedicated Payment Gateway',
        'Unlimited Members & Trainers',
        '24/7 Priority Support'
      ]
    }
  ];

  // Gym gateway form
  const [gatewayData, setGatewayData] = useState({
    gateway_type: 'Razorpay & GPay UPI',
    razorpay_key_id: '',
    razorpay_key_secret: '',
    upi_id: '',
    gpay_number: '',
    merchant_name: '',
    qr_code_url: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sRes, hRes, subRes] = await Promise.all([
        api.get('/api/gym/payment-settings'),
        api.get('/api/saas/subscription-history'),
        api.get('/api/saas/current-subscription')
      ]);
      if (sRes.data.settings) {
        setSettings(sRes.data.settings);
        setGatewayData(sRes.data.settings);
      }
      setSaasHistory(hRes.data.payments || []);
      setCurrentSub(subRes.data.current_subscription || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGymGateway = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/api/gym/payment-settings', gatewayData);
      setSettings(res.data.settings);
      setMsg('Gym Owner Payment Gateway settings saved successfully!');
      setTimeout(() => setMsg(''), 5000);
    } catch (err) {
      alert('Failed to save payment settings');
    }
  };

  // Dynamic Razorpay Checkout Loader
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubscribeSaaSPlan = async (planName, amount) => {
    try {
      setPaying(true);
      const scriptLoaded = await loadRazorpayScript();

      const proceedBackendPayment = async (razorpayPaymentId = null) => {
        const res = await api.post('/api/saas/subscribe-plan', {
          plan_name: planName,
          amount: amount,
          payment_method: razorpayPaymentId ? 'Razorpay Gateway' : 'Razorpay GPay UPI',
          razorpay_payment_id: razorpayPaymentId
        });
        setMsg(res.data.message);
        fetchData();
        setTimeout(() => setMsg(''), 6000);
      };

      if (!scriptLoaded || !window.Razorpay) {
        // Fallback if Razorpay SDK fails to load or offline
        if (window.confirm(`Razorpay SDK loaded in test mode. Confirm subscribing to SaaS ${planName} Plan for ₹${amount}/month?`)) {
          await proceedBackendPayment(`RZP-TEST-${Date.now()}`);
        }
        return;
      }

      // Razorpay Checkout Options
      const options = {
        key: settings?.razorpay_key_id || 'rzp_live_TPB6wlQ8fTh6mu', // Connected Razorpay Key ID
        amount: amount * 100, // Amount in paise

        currency: 'INR',
        name: 'SaaS Platform Admin',
        description: `Subscription Upgrade to ${planName} Plan`,
        handler: async function (response) {
          await proceedBackendPayment(response.razorpay_payment_id);
        },
        prefill: {
          name: user?.name || 'Gym Owner',
          email: user?.email || ''
        },
        theme: {
          color: '#7c3aed'
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.on('payment.failed', function (response) {
        alert('Payment failed: ' + response.error.description);
      });
      paymentObject.open();

    } catch (err) {
      alert('Subscription payment error: ' + (err.response?.data?.error || err.message));
    } finally {
      setPaying(false);
    }
  };

  // Filter plans: ONLY show plans with HIGHER price than current plan!
  const currentPlanPrice = currentSub?.price || 499;
  const upgradePlans = saasPlans.filter(p => p.price > currentPlanPrice);
  const isHighestPlan = upgradePlans.length === 0;

  const memberCheckoutUrl = `${window.location.origin}/checkout/gym/${user?.gym_id || 1}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2">
          <CreditCardIcon className="w-8 h-8 text-orange-600" />
          SaaS Subscriptions & Dual Payment Gateway
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Pay SuperAdmin for your SaaS Subscription and configure your Gym's Razorpay & GPay UPI Gateway for member payments.
        </p>
      </div>

      {msg && (
        <div className="bg-green-100 border border-green-300 text-green-800 p-4 rounded-2xl text-xs font-bold flex items-center gap-2">
          <CheckCircleIcon className="w-5 h-5 text-green-600" />
          {msg}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-3 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('saas_subscription')}
          className={`pb-3 font-bold text-sm border-b-2 transition-all flex items-center gap-2 ${
            activeTab === 'saas_subscription'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          <SparklesIcon className="w-5 h-5" /> SaaS Platform Subscription (Pay Admin)
        </button>

        <button
          onClick={() => setActiveTab('gym_gateway')}
          className={`pb-3 font-bold text-sm border-b-2 transition-all flex items-center gap-2 ${
            activeTab === 'gym_gateway'
              ? 'border-orange-600 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          <CreditCardIcon className="w-5 h-5" /> Your Gym Payment Gateway (Members Pay You)
        </button>

        <button
          onClick={() => setActiveTab('member_link')}
          className={`pb-3 font-bold text-sm border-b-2 transition-all flex items-center gap-2 ${
            activeTab === 'member_link'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          <LinkIcon className="w-5 h-5" /> Member Online Payment Page
        </button>
      </div>

      {/* TAB 1: SaaS Platform Subscription (Pay Admin) */}
      {activeTab === 'saas_subscription' && (
        <div className="space-y-6">

          {/* Current Subscription Card */}
          <div className="bg-gradient-to-r from-purple-900 via-indigo-900 to-slate-900 text-white p-6 rounded-3xl shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="bg-purple-500/30 text-purple-200 text-xs font-black px-3 py-1 rounded-full uppercase tracking-wider border border-purple-400/30">
                  Active Gym SaaS Plan
                </span>
                <span className="bg-emerald-500/20 text-emerald-300 text-xs font-bold px-3 py-1 rounded-full border border-emerald-500/30 flex items-center gap-1">
                  <ShieldCheckIcon className="w-4 h-4" /> {currentSub?.status || 'Active'}
                </span>
              </div>
              <h2 className="text-3xl font-black text-white">
                {currentSub?.plan_name || 'Basic Tier'} <span className="text-lg text-purple-300 font-normal">(₹{currentSub?.price || 499}/month)</span>
              </h2>
              <p className="text-xs text-purple-200 flex flex-wrap items-center gap-4">
                <span className="flex items-center gap-1"><CalendarIcon className="w-4 h-4 text-purple-400" /> Expiry Date: <strong className="text-white">{currentSub?.subscription_end_date ? new Date(currentSub.subscription_end_date).toLocaleDateString() : 'N/A'}</strong></span>
                <span className="flex items-center gap-1"><ClockIcon className="w-4 h-4 text-amber-400" /> <strong className="text-amber-300">{currentSub?.days_left ?? 30} Days Remaining</strong></span>
              </p>
              <p className="text-[11px] text-emerald-300 font-medium pt-1">
                ⚡ Renewal Note: Renewing anytime adds +30 days to your remaining validity (e.g. {currentSub?.days_left ?? 0} days remaining + 30 days = {(currentSub?.days_left ?? 0) + 30} total days)!
              </p>
            </div>

            <div className="flex flex-col gap-2 shrink-0">
              <button
                disabled={paying}
                onClick={() => handleSubscribeSaaSPlan(currentSub?.plan_name || 'Basic Tier', currentSub?.price || 499)}
                className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-xs rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
              >
                <SparklesIcon className="w-4 h-4" />
                {paying ? 'Opening Payment...' : `Renew ${currentSub?.plan_name || 'Current Plan'} (₹${currentSub?.price || 499})`}
              </button>
              <p className="text-[10px] text-center text-purple-200">1-Click Razorpay / GPay UPI</p>
            </div>
          </div>

          {/* Upgrade Section Title */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
                <ArrowUpIcon className="w-5 h-5 text-purple-600" /> Upgrade or Renew SaaS Subscription
              </h3>
              <p className="text-xs text-gray-500">
                You can renew your current tier above or choose a higher plan tier below. Upgrading or renewing extends your validity cumulatively (+30 days).
              </p>
            </div>
          </div>

          {/* Upgrade Pricing Cards */}
          {isHighestPlan ? (
            <div className="bg-purple-50 border border-purple-200 p-8 rounded-3xl text-center space-y-4">
              <SparklesIcon className="w-12 h-12 text-purple-600 mx-auto" />
              <h4 className="text-xl font-black text-purple-950">You are on our Highest Enterprise Plan (₹{currentPlanPrice}/month)!</h4>
              <p className="text-xs text-purple-700 max-w-md mx-auto">
                Enjoy unlimited members, biometric reader integration, and priority WhatsApp automation. Click below to renew your Enterprise plan for another 30 days.
              </p>
              <button
                disabled={paying}
                onClick={() => handleSubscribeSaaSPlan(currentSub?.plan_name || 'Enterprise Chain', currentPlanPrice)}
                className="px-8 py-3 bg-purple-700 hover:bg-purple-800 text-white font-bold text-sm rounded-xl shadow-lg transition-all"
              >
                {paying ? 'Processing...' : `Renew ${currentSub?.plan_name || 'Enterprise Plan'} (₹${currentPlanPrice})`}
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upgradePlans.map((plan) => (
                <div
                  key={plan.name}
                  className={`bg-white border p-6 rounded-3xl shadow-sm hover:shadow-md transition-all flex flex-col justify-between ${
                    plan.recommended ? 'border-2 border-purple-600 relative' : 'border-gray-200'
                  }`}
                >
                  {plan.recommended && (
                    <span className="absolute -top-3 right-4 bg-purple-600 text-white text-[10px] uppercase font-black px-3 py-0.5 rounded-full">
                      Recommended Upgrade
                    </span>
                  )}
                  <div>
                    <span className="text-xs uppercase font-extrabold text-purple-600">{plan.name}</span>
                    <h3 className="text-3xl font-black text-gray-900 mt-1">₹{plan.price} <span className="text-xs text-gray-500 font-normal">/ month</span></h3>
                    <ul className="text-xs text-gray-600 space-y-2.5 mt-4">
                      {plan.features.map((feat, idx) => (
                        <li key={idx} className="flex items-center gap-1.5">
                          <CheckCircleIcon className="w-4 h-4 text-purple-600 shrink-0" />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    disabled={paying}
                    onClick={() => handleSubscribeSaaSPlan(plan.name, plan.price)}
                    className="w-full mt-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-2"
                  >
                    <ArrowUpIcon className="w-4 h-4" />
                    {paying ? 'Opening Razorpay...' : `Upgrade to ${plan.name} (₹${plan.price})`}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* History */}
          <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm">
            <h3 className="text-md font-bold text-gray-900 mb-3">SaaS Subscription Payment History</h3>
            {saasHistory.length === 0 ? (
              <p className="text-xs text-gray-500">No previous platform payments found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-gray-50 font-bold text-gray-600">
                    <tr>
                      <th className="p-3">Transaction ID</th>
                      <th className="p-3">Plan</th>
                      <th className="p-3">Amount</th>
                      <th className="p-3">Method</th>
                      <th className="p-3">Date</th>
                      <th className="p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {saasHistory.map((p) => (
                      <tr key={p.id}>
                        <td className="p-3 font-mono text-purple-900 font-medium">{p.transaction_id}</td>
                        <td className="p-3 font-bold">{p.plan_name}</td>
                        <td className="p-3 font-bold text-emerald-600">₹{p.amount}</td>
                        <td className="p-3">{p.payment_method}</td>
                        <td className="p-3">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : '-'}</td>
                        <td className="p-3"><span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full">Completed</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: Gym Owner Gateway Settings */}
      {activeTab === 'gym_gateway' && (
        <div className="bg-white border border-gray-200 p-6 rounded-3xl shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b pb-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Member Payment Acceptance Setup</h2>
              <p className="text-xs text-gray-500">
                Configure your own Razorpay & GPay UPI credentials so members pay directly into your bank account.
              </p>
            </div>
            <span className="bg-green-100 text-green-800 text-xs font-bold px-3 py-1 rounded-full border border-green-300">
              Gateway Active
            </span>
          </div>

          <form onSubmit={handleSaveGymGateway} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Payment Gateway Type</label>
                <select
                  value={gatewayData.gateway_type}
                  onChange={(e) => setGatewayData({ ...gatewayData, gateway_type: e.target.value })}
                  className="w-full text-sm border p-2.5 rounded-xl font-medium"
                >
                  <option value="Razorpay & GPay UPI">Razorpay & GPay UPI (Recommended)</option>
                  <option value="Stripe & GPay">Stripe & GPay</option>
                  <option value="UPI Direct QR">Direct GPay / PhonePe UPI QR</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Merchant Display Name</label>
                <input
                  type="text"
                  placeholder="e.g. Royal Gold Gym Solutions"
                  value={gatewayData.merchant_name || ''}
                  onChange={(e) => setGatewayData({ ...gatewayData, merchant_name: e.target.value })}
                  className="w-full text-sm border p-2.5 rounded-xl"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">GPay / PhonePe / Paytm UPI ID</label>
                <input
                  type="text"
                  placeholder="e.g. gymowner@okicici or 9876543210@paytm"
                  value={gatewayData.upi_id || ''}
                  onChange={(e) => setGatewayData({ ...gatewayData, upi_id: e.target.value })}
                  className="w-full text-sm border p-2.5 rounded-xl font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">GPay Phone Number</label>
                <input
                  type="text"
                  placeholder="e.g. +91 98765 43210"
                  value={gatewayData.gpay_number || ''}
                  onChange={(e) => setGatewayData({ ...gatewayData, gpay_number: e.target.value })}
                  className="w-full text-sm border p-2.5 rounded-xl font-mono"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Razorpay Key ID</label>
                <input
                  type="text"
                  placeholder="rzp_live_..."
                  value={gatewayData.razorpay_key_id || ''}
                  onChange={(e) => setGatewayData({ ...gatewayData, razorpay_key_id: e.target.value })}
                  className="w-full text-sm border p-2.5 rounded-xl font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Razorpay Key Secret</label>
                <input
                  type="password"
                  placeholder="••••••••••••••••"
                  value={gatewayData.razorpay_key_secret || ''}
                  onChange={(e) => setGatewayData({ ...gatewayData, razorpay_key_secret: e.target.value })}
                  className="w-full text-sm border p-2.5 rounded-xl font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">GPay / UPI QR Code Image URL</label>
              <input
                type="text"
                placeholder="https://example.com/my-gpay-qr.png"
                value={gatewayData.qr_code_url || ''}
                onChange={(e) => setGatewayData({ ...gatewayData, qr_code_url: e.target.value })}
                className="w-full text-sm border p-2.5 rounded-xl font-mono"
              />
            </div>

            <button
              type="submit"
              className="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl shadow-sm transition-all text-sm"
            >
              Save Gym Payment Gateway
            </button>
          </form>
        </div>
      )}

      {/* TAB 3: Member Online Payment Page Generator */}
      {activeTab === 'member_link' && (
        <div className="bg-white border border-gray-200 p-6 rounded-3xl shadow-sm space-y-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-blue-600" /> Shareable Member Online Payment URL
          </h2>
          <p className="text-xs text-gray-600">
            Send this URL to your members so they can directly pay their membership fee online via your GPay UPI or Razorpay Gateway!
          </p>

          <div className="flex items-center gap-2 bg-gray-50 border p-3 rounded-xl">
            <input
              type="text"
              readOnly
              value={memberCheckoutUrl}
              className="w-full text-xs font-mono bg-transparent border-none outline-none text-gray-800"
            />
            <button
              onClick={() => { navigator.clipboard.writeText(memberCheckoutUrl); alert('Payment URL copied to clipboard!'); }}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition-all"
            >
              Copy Link
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
